import { 
  AvailableProducts, 
  OrderConfiguration, 
  TotalCalculation,
  PriceBreakdown 
} from '../services/order-configuration';

/**
 * Calculate order total on the frontend using already-loaded product data.
 * 
 * This eliminates the need for API calls and database queries during
 * order configuration, providing instant calculation feedback.
 * 
 * @param configuration - Current order configuration (selections)
 * @param availableProducts - Products loaded from getAvailableProducts API
 * @param foodParcelPrice - Base food parcel price
 * @param foodParcelName - Optional food parcel name for breakdown
 * @returns Calculated total with detailed breakdown
 */
function num(x: unknown): number {
  const n = Number(x);
  return typeof x === 'number' && !Number.isNaN(x) ? x : (Number.isNaN(n) ? 0 : n);
}

export function calculateOrderTotal(
  configuration: OrderConfiguration,
  availableProducts: AvailableProducts,
  foodParcelPrice: number,
  foodParcelName?: string
): TotalCalculation {
  const fpPrice = num(foodParcelPrice);
  let total = fpPrice;
  const breakdown: PriceBreakdown = {
    food_parcel: {
      name: foodParcelName ?? '',
      price: fpPrice,
    },
  };

  // Bundles: include in breakdown whenever at least one is selected (with names for checkout display)
  if (configuration.bundle_selections && configuration.bundle_selections.length > 0) {
    let bundleTotal = 0;
    let bundleCount = 0;
    const bundleItems: Array<{ id: number; name: string; price: number }> = [];

    configuration.bundle_selections.forEach((bundleId) => {
      if (bundleId !== null && bundleId !== undefined) {
        const bundle = availableProducts.bundles.find((b) => b.id === bundleId);
        if (bundle) {
          const price = num(bundle.bundle_price);
          bundleTotal += price;
          bundleCount++;
          bundleItems.push({
            id: bundle.id,
            name: bundle.name,
            price,
          });
        }
      }
    });

    if (bundleCount > 0) {
      total += bundleTotal;
      breakdown.bundles = {
        total: bundleTotal,
        count: bundleCount,
        items: bundleItems,
      };
    }
  }

  // Bread: include in breakdown when selected (even if $0)
  if (configuration.bread_selection !== null && configuration.bread_selection !== undefined) {
    const bread = availableProducts.breads.find((b) => b.id === configuration.bread_selection);
    if (bread) {
      const breadPrice = num(bread.food_parcel_price);
      total += breadPrice;
      breakdown.bread = {
        name: bread.name,
        price: breadPrice,
      };
    }
  }

  // Grocery: include in breakdown whenever at least one item is selected
  if (configuration.grocery_selections && configuration.grocery_selections.length > 0) {
    let groceryTotal = 0;
    const groceryItems: Array<{ name: string; price: number }> = [];

    configuration.grocery_selections.forEach((productId) => {
      if (productId !== null && productId !== undefined) {
        const product = availableProducts.grocery_items.find((g) => g.id === productId);
        if (product) {
          const price = num(product.food_parcel_price);
          groceryTotal += price;
          groceryItems.push({
            name: product.name,
            price,
          });
        }
      }
    });

    if (groceryItems.length > 0) {
      total += groceryTotal;
      breakdown.grocery = {
        total: groceryTotal,
        items: groceryItems,
      };
    }
  }

  // Add-ons (parcel_items): include in breakdown whenever at least one is selected
  if (configuration.parcel_item_selections && Object.keys(configuration.parcel_item_selections).length > 0) {
    let parcelItemTotal = 0;
    const parcelItems: Array<{ name: string; price: number }> = [];

    Object.entries(configuration.parcel_item_selections).forEach(([itemIdStr, selected]) => {
      if (selected) {
        const itemId = parseInt(itemIdStr, 10);
        if (!Number.isNaN(itemId)) {
          const item = availableProducts.parcel_items.find((p) => p.id === itemId);
          if (item) {
            const price = num(item.price);
            parcelItemTotal += price;
            parcelItems.push({
              name: item.name,
              price,
            });
          }
        }
      }
    });

    if (parcelItems.length > 0) {
      total += parcelItemTotal;
      breakdown.parcel_items = {
        total: parcelItemTotal,
        items: parcelItems,
      };
    }
  }

  // Add-ons (products from product list): include in breakdown when at least one has quantity > 0
  const addonSelections = configuration.addon_product_selections ?? {};
  const addonProducts = availableProducts.addon_products ?? [];
  if (Object.keys(addonSelections).length > 0 && addonProducts.length > 0) {
    let addonTotal = 0;
    const addonItems: Array<{ product_id: number; name: string; quantity: number; unit_price: number; line_total: number }> = [];

    Object.entries(addonSelections).forEach(([productIdStr, quantity]) => {
      const qty = typeof quantity === 'number' ? quantity : parseInt(String(quantity), 10) || 0;
      if (qty > 0) {
        const productId = parseInt(productIdStr, 10);
        if (!Number.isNaN(productId)) {
          const product = addonProducts.find((p) => p.id === productId);
          if (product) {
            const unitPrice = num(product.add_on_price);
            const lineTotal = unitPrice * qty;
            addonTotal += lineTotal;
            addonItems.push({
              product_id: product.id,
              name: product.name,
              quantity: qty,
              unit_price: unitPrice,
              line_total: lineTotal,
            });
          }
        }
      }
    });

    if (addonItems.length > 0) {
      total += addonTotal;
      breakdown.addon_products = {
        total: addonTotal,
        items: addonItems,
      };
    }
  }

  // Round to 2 decimal places to avoid floating point precision issues
  total = Number.isFinite(total) ? Math.round(total * 100) / 100 : 0;

  return {
    total,
    breakdown,
  };
}
